import React from 'react';
import LegalPageLayout from './homepage/LegalPageLayout';

const EFFECTIVE_DATE = "May 11, 2026";

function Terms() {
  const contents = [
    { id: "s1", title: "Acceptance of Terms" },
    { id: "s2", title: "Description of Service" },
    { id: "s3", title: "Accounts & Eligibility" },
    { id: "s4", title: "Acceptable Use" },
    { id: "s5", title: "Third-Party Services" },
    { id: "s6", title: "Intellectual Property" },
    { id: "s7", title: "Disclaimers" },
    { id: "s8", title: "Limitation of Liability" },
    { id: "s9", title: "Termination" },
    { id: "s10", title: "Changes to These Terms" },
    { id: "s11", title: "Governing Law" },
    { id: "s12", title: "Contact" },
  ];

  return (
    <LegalPageLayout
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
      description={
        <>
          These Terms of Service ("Terms") govern your access to and use of <strong>BookBridge</strong>
          {" "}(the "Service") provided by <strong>XcelerateAI Inc.</strong> ("we", "us", or "our"). By using the
          Service, you agree to be bound by these Terms.
        </>
      }
    >
      {/* Table of Contents */}
      <nav className="bg-gradient-to-br from-soft to-white border border-line rounded-2xl p-8 mb-12 shadow-sm" aria-label="Table of contents">
        <h4 className="text-lg font-bold text-navy mb-6">Contents</h4>
        <ol className="space-y-3">
          {contents.map((item, index) => (
            <li key={item.id}>
              <a 
                href={`#${item.id}`} 
                className="text-ink hover:text-indigo-600 transition-colors font-medium"
              >
                {index + 1}. {item.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* Content Sections */}
      <section id="s1" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">1. Acceptance of Terms</h2>
        <p className="text-lg text-muted leading-relaxed">
          By accessing, installing, deploying, or using the Service, you confirm that you have read, understood,
          and agree to be bound by these Terms. If you are using the Service on behalf of an organization, you
          represent that you have authority to bind that organization to these Terms.
        </p>
      </section>

      <section id="s2" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">2. Description of Service</h2>
        <p className="text-lg text-muted leading-relaxed">
          BookBridge is a developer middleware that enables applications to authenticate with
          BookBridge via OAuth 2.0 and to perform accounting operations through the API. The
          Service is provided as software you deploy and operate; we do not host accounting data on your behalf
          unless explicitly stated otherwise.
        </p>
      </section>

      <section id="s3" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">3. Accounts & Eligibility</h2>
        <ul className="space-y-3 text-lg text-muted">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>You must be at least 18 years old or the age of majority in your jurisdiction.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>You are responsible for keeping your credentials, JWT secrets, and OAuth client secrets confidential.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>You are responsible for all activity that occurs under your account or deployment.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>You must promptly notify us of any unauthorized access or security breach.</span>
          </li>
        </ul>
      </section>

      <section id="s4" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">4. Acceptable Use</h2>
        <p className="text-lg text-muted leading-relaxed mb-4">You agree NOT to use the Service to:</p>
        <ul className="space-y-3 text-lg text-muted">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Violate any applicable law, regulation, or third-party rights.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Access BookBridge data you are not authorized to access.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Reverse engineer, attack, or attempt to disrupt the Service or its systems.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Resell, sublicense, or misrepresent the Service as your own without permission.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Send spam, malware, or otherwise harmful content through the Service.</span>
          </li>
        </ul>
      </section>

      <section id="s5" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">5. Third-Party Services</h2>
        <p className="text-lg text-muted leading-relaxed">
          The Service integrates with <strong>BookBridge</strong> and its developer platform. Your
          use of BookBridge data is also governed by{" "}
          <a href="https://www.intuit.com/legal/terms/" target="_blank" rel="noreferrer" className="text-indigo-600 underline underline-offset-2 hover:no-underline">BookBridge's Terms of Service</a>.
          We are not responsible for the availability, accuracy, or behavior of third-party services.
        </p>
      </section>

      <section id="s6" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">6. Intellectual Property</h2>
        <p className="text-lg text-muted leading-relaxed">
          All rights, title, and interest in the Service — including its source code, trademarks, and
          documentation — remain the property of XcelerateAI Inc. and its licensors, except as expressly
          granted under an applicable open-source or commercial license. You retain all rights to data you
          submit or process through the Service.
        </p>
      </section>

      <section id="s7" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">7. Disclaimers</h2>
        <p className="text-lg text-muted leading-relaxed">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING WITHOUT LIMITATION ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
          NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, or secure.
        </p>
      </section>

      <section id="s8" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">8. Limitation of Liability</h2>
        <p className="text-lg text-muted leading-relaxed">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL XCELERATEAI BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
          DATA, OR GOODWILL ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL AGGREGATE LIABILITY
          WILL NOT EXCEED THE AMOUNT YOU PAID US (IF ANY) IN THE 12 MONTHS PRECEDING THE CLAIM.
        </p>
      </section>

      <section id="s9" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">9. Termination</h2>
        <p className="text-lg text-muted leading-relaxed">
          We may suspend or terminate your access to the Service at any time, with or without notice, for
          conduct that we believe violates these Terms or is harmful to other users, us, or third parties. You
          may stop using the Service at any time. Sections that by their nature should survive termination
          (including IP, disclaimers, and liability) will survive.
        </p>
      </section>

      <section id="s10" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">10. Changes to These Terms</h2>
        <p className="text-lg text-muted leading-relaxed">
          We may update these Terms from time to time. When we do, we will revise the "Last updated" date above.
          Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
        </p>
      </section>

      <section id="s11" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">11. Governing Law</h2>
        <p className="text-lg text-muted leading-relaxed">
          These Terms are governed by the laws of the jurisdiction in which XcelerateAI Inc. is established,
          without regard to its conflict-of-law principles. Any disputes will be resolved in the courts of that
          jurisdiction, unless otherwise required by applicable law.
        </p>
      </section>

      <section id="s12" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">12. Contact</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">For questions about these Terms, contact us at:</p>
        <div className="bg-gradient-to-br from-soft to-white p-8 rounded-2xl border border-line shadow-sm">
          <p className="text-lg mb-2"><strong>XcelerateAI Inc.</strong></p>
          <p className="text-lg text-muted">
            Email: <a href="mailto:support@xcelerateai.com" className="text-indigo-600 underline underline-offset-2 hover:no-underline">support@xcelerateai.com</a>
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
}

export default Terms;
